import { calculateGpi, calculateVolume, solveInchesFromVolume } from "@/lib/tank";

// "Danger zone" chart rows, mystery-tank fingerprints and field corrections.
const EPS = 1 / 16;
export const zoneOf = (h, d, h90, h95) => {
  if (h >= h95 - EPS) return "red";
  if (h >= h90 - EPS) return "orange";
  const depth = d > 0 ? h / d : 0;
  if (depth >= 0.35 && depth <= 0.65) return "belly";
  if (depth >= 0.75 && depth < 0.9) return "curve";
  return "";
};

// Whole inches, with finer ticks from 30" to 90" (belly) where one inch is 100+ gallons.
export function chartHeights(d, fine = 0.125) {
  const out = [];
  const top = Math.floor(d);
  for (let i = 1; i <= top; i++) {
    out.push(i);
    if (i >= 30 && i < 90 && i < top) for (let k = 1; k * fine < 1 - 1e-9; k++) out.push(i + k * fine);
  }
  return out;
}

export function buildChart(cap, d, ends, fine = 0.125) {
  const cap90 = cap * 0.9, cap95 = cap * 0.95;
  const h90 = solveInchesFromVolume(cap90, d, cap, ends), h95 = solveInchesFromVolume(cap95, d, cap, ends);
  const rows = chartHeights(d, fine).map((h) => {
    const vol = calculateVolume(h, d, cap, ends);
    const at90 = h >= h90 - EPS;
    return { h, vol, gpi: calculateGpi(h, d, cap, ends), pct: cap > 0 ? (vol / cap) * 100 : 0,
      room90: at90 ? Math.min(0, cap90 - vol) : cap90 - vol, to90: at90 ? 0 : h90 - h, zone: zoneOf(h, d, h90, h95) };
  });
  return { rows, h90, h95, cap90, cap95 };
}

// Field references (user-supplied); the app recalculates each and flags differences.
export const FINGERPRINTS = [
  { id: "8k-steel", name: "8,000 gal · 8' steel", ends: "flat", d: 96, idLabel: '96"', max: 8038, h90: 81.3, gpi: 106, stick: 12 },
  { id: "10k-steel", name: "10,000 gal · 8' steel", ends: "flat", d: 96, idLabel: '96"', max: 10048, h90: 81.3, gpi: 133, stick: 12 },
  { id: "10k-fg", name: "10,000 gal · 8' fiberglass", ends: "dome", d: 93.25, idLabel: '92.5"–94"', max: 9760, h90: 79.5, gpi: 130, stick: 12 },
  { id: "12k-steel", name: "12,000 gal · 8' steel", ends: "flat", d: 96, idLabel: '96"', max: 11860, h90: 81.3, gpi: 157, stick: 12 },
  { id: "12k-fg", name: "12,000 gal · 10' fiberglass", ends: "dome", d: 117.25, idLabel: '116.5"–118"', max: 11880, h90: 100.1, gpi: 126, stick: 15 },
  { id: "15k-steel", name: "15,000 gal · 10' steel", ends: "flat", d: 120, idLabel: '120"', max: 15155, h90: 101.6, gpi: 158, stick: 15 },
  { id: "20k-steel", name: "20,000 gal · 10' steel", ends: "flat", d: 120, idLabel: '120"', max: 20100, h90: 101.6, gpi: 210, stick: 15 },
  { id: "30k-steel", name: "30,000 gal · 10.5' steel", ends: "flat", d: 126, idLabel: '126"', max: 30085, h90: 106.7, gpi: 296, stick: 15 },
];

export function checkFingerprint(f) {
  const calcH90 = solveInchesFromVolume(f.max * 0.9, f.d, f.max, f.ends);
  const calcGpi = calculateGpi(f.d / 2, f.d, f.max, f.ends);
  const flags = [];
  if (Math.abs(calcH90 - f.h90) > 1) flags.push("h90");
  if (Math.abs(calcGpi - f.gpi) / f.gpi > 0.06) flags.push("gpi");
  return { ...f, calcH90, calcGpi, flags };
}

export const FINGERPRINTS_CHECKED = FINGERPRINTS.map(checkFingerprint);
export const rankByGpi = (gpi) => FINGERPRINTS_CHECKED
  .map((f) => ({ ...f, off: Math.abs(f.calcGpi - gpi) / f.calcGpi })).sort((a, b) => a.off - b.off);

// Tilt: a riser near the low end reads high by half the end-to-end fall; the high end reads low.
export const RISER_SHIFT = { low: 0.5, lowq: 0.25, center: 0, highq: -0.25, high: -0.5 };
export function tiltFix(stick, tilt, riser, d, cap, ends) {
  const offset = tilt * (RISER_SHIFT[riser] ?? 0);
  const level = Math.max(0, Math.min(d, stick - offset));
  const gal = calculateVolume(level, d, cap, ends) - calculateVolume(stick, d, cap, ends);
  const valid = stick > Math.abs(tilt) / 2 && stick < d - Math.abs(tilt) / 2;
  return { offset, level, gal, valid };
}

export const STICK_12 = 144, STICK_15 = 180;
export const stickNeed = (bury, d) => {
  const need = (bury || 0) + d;
  return { need, level: need > STICK_15 ? "over15" : need > STICK_12 ? "need15" : "ok" };
};

export const THERMAL = { gas: 0.00069, diesel: 0.00046 };
export const shrinkGal = (gal, rackF, tankF, kind) => gal * THERMAL[kind] * (rackF - tankF);
export const thermalKind = (product) => (/diesel|kero/i.test(product || "") ? "diesel" : "gas");
